/* eslint-disable sonarjs/no-duplicate-string */
import { useDisclosure } from '@mantine/hooks';
import {
  Box, Button, Flex, Text
} from '@huspy/briks-web';
import { useTranslation } from 'react-i18next';
import { useForm, zodResolver } from '@mantine/form';
import { BaseSlido } from '@modules/core/components/BaseSlido';
import {
  UserPlus, UserSquare,
  Plus
} from '@huspy/briks-icons';
import {
  CaseFormInput,
  CaseFormSelectInput,
} from '@modules/opportunities/presentation/pages/CaseCreation/components/CaseInputs';
import { useLocation, useNavigate } from '@tanstack/react-router';
import { scrollToError } from '@shared/utils';
import { useOpportunityFormErrorHandler } from '@modules/opportunities/presentation/hooks/useHandleErrors';
import { useEffect, useState } from 'react';
import { slidoDeepLink } from '@modules/core/utils';
import { Tabs } from '@mantine/core';
import briksTheme from '@shared/briks-theme';
import { clearAmplitudeEventContext } from '@shared/analytics/amplitude';
import { ExistingClientSelect } from '@modules/clients/presentation/v2/features/ExistingClientSelect';
import useCreateProposal from '@modules/proposals/api/mutation/useCreateProposal';
import { CITIZEN_STATE, TYPE_OF_EMPLOYMENT } from '@modules/banks/presentation/v1/features/BankProducts/enums';
import { ProposalCreationClientAndMortgageRoute } from '@modules/proposals/presentation/routes';
import { createProposalSchema } from './validator';
import { ProposalCreationSlidoProps, ExistingClient } from '../types';

export const ProposalCreationSlidoAe = ({
  hideTabs = false,
  tab = 'newClient',
  existingClientData,
  actionButton,
}: ProposalCreationSlidoProps) => {
  const { hash } = useLocation();
  const [opened, { open, close }] = useDisclosure(hash === slidoDeepLink.PROPOSAL_CREATION);
  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  const navigate = useNavigate();
  const [activeTab, setActiveTab] = useState<string | null>(tab);
  const {
    mutateAsync,
    error,
    isPending,
  } = useCreateProposal();

  const { t } = useTranslation();
  const form = useForm({
    initialValues: {
      client_external_id: existingClientData?.id ?? '',
      first_name: existingClientData?.firstName ?? '',
      last_name: existingClientData?.lastName ?? '',
      residency_status: '',
      employment_status: '',
    },
    validateInputOnChange: true,
    validate: zodResolver(createProposalSchema),
  });

  const onSubmit = async () => {
    clearAmplitudeEventContext();

    const data = form.values;

    form.validate();
    if (!form.isValid()) {
      scrollToError(form.errors);
      return;
    }

    const res = await mutateAsync({
      first_name: data.first_name,
      last_name: data.last_name,
      employment_status: data.employment_status,
      residency_status: data.residency_status,
      client_external_id: data.client_external_id || undefined,
    });

    navigate({
      to: ProposalCreationClientAndMortgageRoute.to,
      params: { id: res.external_id },
    });
  };

  useOpportunityFormErrorHandler({
    opportunityErrors: error,
    form,
  });

  useEffect(() => {
    if (!hideTabs) {
      form.setValues({
        ...form.values,
        first_name: '',
        last_name: '',
        client_external_id: '',
        employment_status: '',
        residency_status: '',
      });
    }

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [activeTab]);

  return (
    <>
      <BaseSlido
        opened={ opened }
        close={ close }
        title={ (
          <Flex direction='column' gap='2' p='6' pb='0'>
            <Text size='5xl'>
              {t('proposals.proposalsCreate.title')}
            </Text>
          </Flex>
        ) }
        hasDivider={ hideTabs }
        footer={ (
          <Flex justify='end' gap='2'>
            <Button
              type='button'
              onClick={ close }
              variant='secondary'
              data-test='discard-btn'
            >
              {t('clients.formSections.discardButton')}
            </Button>
            <Button
              type='submit'
              disabled={ isPending }
              loading={ isPending }
              variant={ isPending ? 'secondary' : 'primary' }
              data-test='save-btn'
              onClick={ onSubmit }
            >
              {t('proposals.proposalsCreate.title')}
            </Button>
          </Flex>
        ) }
      >
        <Flex h='100%' w='100%'>
          <Tabs defaultValue={ tab } onChange={ setActiveTab } w='100%'>
            <Tabs.List display={ hideTabs ? 'none' : 'display' }>
              <Tabs.Tab
                value='newClient'
                leftSection={ (
                  <Flex align='center' justify='center' h='100%' w='100%' mr='1'>
                    <UserPlus color={ briksTheme.colors['neutral.600'] } />
                  </Flex>
                ) }
                py='8px'
                pl='24px'
              >
                <Text size='md' mr='4' color='neutral.600' whiteSpace='nowrap'>
                  {t('proposals.proposalsCreate.title')}
                </Text>
              </Tabs.Tab>
              <Tabs.Tab
                value='existingClient'
                leftSection={ (
                  <Flex align='center' justify='center' h='100%' w='100%' mr='1'>
                    <UserSquare color={ briksTheme.colors['neutral.600'] } />
                  </Flex>
                ) }
              >
                <Text size='md' mr='4' color='neutral.600' whiteSpace='nowrap'>
                  {t('clients.formSections.existingClientTab')}
                </Text>
              </Tabs.Tab>
            </Tabs.List>
            <form onSubmit={ form.onSubmit(onSubmit, scrollToError) }>
              <Flex direction='column' gap='4' p='6'>
                <Tabs.Panel value='newClient'>
                  <Flex gap='4'>
                    <CaseFormInput
                      name='first_name'
                      label='firstName'
                      data-test='input-firstname'
                      form={ form }
                    />
                    <CaseFormInput
                      name='last_name'
                      label='lastName'
                      data-test='input-lastname'
                      form={ form }
                    />
                  </Flex>
                </Tabs.Panel>
                <Tabs.Panel value='existingClient'>
                  <ExistingClientSelect
                    label={ t('clients.formSections.selectClient') }
                    placeholder={ t('clients.formSections.selectClient') }
                    onValueChange={ (clientData: ExistingClient) => {
                      form.setValues({
                        ...form.values,
                        first_name: clientData!.firstName,
                        last_name: clientData!.lastName,
                        client_external_id: clientData!.id,
                      });
                    } }
                    defaultValue={ existingClientData }
                    disabled={ !!existingClientData }
                    isError={ !!form.errors?.first_name }
                  />
                </Tabs.Panel>
                <Flex gap='4'>
                  <CaseFormSelectInput
                    name='residency_status'
                    data-test='select-residency-status'
                    label='residencyStatus'
                    form={ form }
                    data={ [
                      CITIZEN_STATE.uaeResident,
                      CITIZEN_STATE.nonResident,
                      CITIZEN_STATE.uaeNational
                    ] }
                  />
                  <CaseFormSelectInput
                    name='employment_status'
                    data-test='select-employment-status'
                    label='employmentStatus'
                    form={ form }
                    data={ [
                      TYPE_OF_EMPLOYMENT.salaried,
                      TYPE_OF_EMPLOYMENT.selfEmploymed
                    ] }
                  />
                </Flex>
              </Flex>
            </form>
          </Tabs>
        </Flex>
      </BaseSlido>
      {actionButton
        ? (
          <Box onClick={ open }>
            {actionButton}
          </Box>
        )
        : (
          <Button onClick={ open } data-test='create-proposal-btn'>
            <Plus width={ 24 } color='inherit' />
            {t('proposals.createProposal')}
          </Button>
        )}

    </>
  );
};
